/**
 * CortisolTrendCard — посоката на кортизола за 7 дни и подсказката на коуча,
 * слети в кортизоловия близнак (замества старата страница Cortisol Tracker).
 * Данни: GET /api/cortisol-tracker/trend · /coach-nudge · POST /symptom
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingDown, Bell, Plus, Loader2 } from "lucide-react";
import { useAuth } from "@/App";

const QUICK = [
  ["fatigue", "Умора"], ["insomnia", "Безсъние"], ["sugar_craving", "Сладко"],
  ["anxiety", "Тревожност"], ["brain_fog", "Мъгла"], ["muscle_tension", "Напрежение"],
  ["irritability", "Раздразнителност"], ["headache", "Главоболие"],
];

export default function CortisolTrendCard({ isDark = false }) {
  const { API } = useAuth();
  const [trend, setTrend] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, n] = await Promise.all([
        axios.get(`${API}/cortisol-tracker/trend?days=7`),
        axios.get(`${API}/cortisol-tracker/coach-nudge`).catch(() => null),
      ]);
      setTrend(t.data);
      setNudge(n?.data?.nudge || n?.data || null);
    } catch { setTrend(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!trend) return null;

  const series = trend.series || [];
  const max = Math.max(40, ...series.map((d) => d.percent || d.percent_adjusted || 0));
  const border = isDark ? "border-white/10" : "border-slate-200";

  const logSymptom = async (key) => {
    setBusy(true);
    try {
      await axios.post(`${API}/cortisol-tracker/symptom`, { symptom_key: key, severity: 2 });
      toast.success("Записано в кортизоловия дневник");
      load();
    } catch { toast.error("Не се записа"); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
      data-testid="cortisol-trend-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
          <TrendingDown className="w-4 h-4 text-orange-500" /> Посока на кортизола за 7 дни
        </span>
        {trend.avg_percent != null && (
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-600"
            data-testid="cortisol-trend-avg">
            средно {Math.round(trend.avg_percent)}%
          </span>
        )}
      </div>

      <div className="flex items-end gap-1.5 h-24 mt-3" data-testid="cortisol-trend-chart">
        {series.map((d) => {
          const v = d.percent ?? 0;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-md bg-gradient-to-t from-orange-500 to-amber-400"
                style={{ height: `${Math.max(4, (v / max) * 80)}px` }}
                title={`${d.date}: ${Math.round(v)}%`} />
              <span className="text-[8px] opacity-60">{(d.date || "").slice(8)}</span>
            </div>
          );
        })}
      </div>

      {nudge?.message_bg && (
        <div className="mt-3 p-2.5 rounded-lg bg-amber-500/10 text-[11px] flex items-start gap-1.5"
          data-testid="cortisol-trend-nudge">
          <Bell className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
          <span>{nudge.message_bg}</span>
        </div>
      )}

      <div className={`mt-3 pt-3 border-t ${border}`}>
        <div className="text-[10px] font-bold uppercase opacity-60 mb-1.5">
          Отбележи симптом за днес
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map(([k, label]) => (
            <button key={k} disabled={busy} onClick={() => logSymptom(k)}
              className={`px-2 py-1 rounded-full text-[10px] font-bold border transition
                ${border} hover:border-orange-500 hover:text-orange-600 disabled:opacity-50`}
              data-testid={`cortisol-trend-log-${k}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 inline" />} {label}
            </button>
          ))}
        </div>
        <div className="text-[9px] opacity-50 mt-1.5">
          Симптомите от профила в „Кортизолова интелигентност“ влизат автоматично тук — един дневник.
        </div>
      </div>
    </div>
  );
}
